import type {ReactNode} from 'react';
import {ShieldCheck} from 'lucide-react';
import ReactMarkdown, {type Components} from 'react-markdown';

const MARKDOWN_COMPONENTS: Components = {
  h1: ({children}) => <h1 className='text-xl font-semibold mt-4 mb-3'>{children}</h1>,
  h2: ({children}) => <h2 className='text-lg font-semibold mt-4 mb-3'>{children}</h2>,
  h3: ({children}) => <h3 className='text-base font-semibold mt-3 mb-2'>{children}</h3>,
  p: ({children}) => <p className='mb-3'>{children}</p>,
  ul: ({children}) => <ul className='list-disc pl-5 mb-3 space-y-1'>{children}</ul>,
  ol: ({children}) => <ol className='list-decimal pl-5 mb-3 space-y-1'>{children}</ol>,
  li: ({children}) => <li className='leading-relaxed'>{children}</li>,
  strong: ({children}) => <strong className='font-semibold'>{children}</strong>,
};

type Props = {
  body: string;
  prepTip?: string | null;
  /** Rendered at the very bottom of the expanded body (e.g. a company-guide CTA). */
  footer?: ReactNode;
};

/** Shared expanded-experience body: markdown narrative, prep tip, AI-anonymized note. */
export function ExperienceBody({body, prepTip, footer}: Props) {
  return (
    <div className='px-5 pb-5 border-t border-iq-grey-light'>
      <div className='text-iq-grey-dark leading-relaxed mt-4'>
        <ReactMarkdown components={MARKDOWN_COMPONENTS}>{body}</ReactMarkdown>
      </div>

      {prepTip && (
        <div
          className='mt-4 rounded-md p-4 border'
          style={{borderColor: '#fcd34d', backgroundColor: '#fffbeb'}}>
          <p
            className='text-xs font-bold uppercase tracking-wide mb-2'
            style={{color: '#d97706'}}>
            Prep tip from this candidate
          </p>
          <p className='text-iq-grey-dark'>{prepTip}</p>
        </div>
      )}

      <div className='mt-4 flex justify-end'>
        <div className='relative group inline-flex items-center gap-1 cursor-default'>
          <ShieldCheck size={12} className='text-iq-grey-medium' />
          <span className='text-xs text-iq-grey-medium'>AI-anonymized</span>
          {/* Tooltip */}
          <div className='absolute bottom-full right-0 mb-2 w-72 hidden group-hover:block z-10'>
            <div className='bg-gray-900 text-white text-sm rounded-lg p-4 shadow-lg'>
              <p className='font-semibold mb-2'>Why do these read similarly?</p>
              <p className='text-gray-300 leading-relaxed'>
                Some candidate experiences are processed through AI to remove identifying details
                including names, teams, interviewer descriptions, and any other identifying
                information while preserving the substance of what happened. This protects candidate
                anonymity but means the writing style is normalized across reports.
              </p>
            </div>
            {/* Arrow */}
            <div className='flex justify-end pr-3'>
              <div className='w-2.5 h-2.5 bg-gray-900 rotate-45 -mt-1.5' />
            </div>
          </div>
        </div>
      </div>

      {footer}
    </div>
  );
}
